import {
  Button,
  Divider,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { PlaylistAddRounded, UpdateRounded } from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import {
  createApprovalTypeAsync,
  getApprovalTypeAsync,
  updateApprovalTypeAsync
} from './approvalTypeSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    paper: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: '16px'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: 32
    }
  })
);

interface CreateEditApprovalType {
  id: number | null;
  name: string | null;
}

export default function ApprovalTypeCreateEdit() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { selectedApprovalType } = useSelector(
    (state: RootState) => state.approvalTypes
  );

  const params = useParams<{ approvalTypeId: string }>();
  const approvalTypeId = Number.parseInt(params.approvalTypeId, 10);
  const isNewPage = !Boolean(approvalTypeId);

  const [approvalType, setApprovalType] = useState<CreateEditApprovalType>({
    id: null,
    name: null
  });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isNewPage && selectedApprovalType) {
      setApprovalType({
        ...approvalType,
        id: selectedApprovalType.id,
        name: selectedApprovalType.name
      });
    }
  }, [selectedApprovalType]);

  useEffect(() => {
    if (!isNewPage) dispatch(getApprovalTypeAsync(approvalTypeId));
  }, []);

  function onChangeField(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setApprovalType({
      ...approvalType,
      [name]: value === '' ? null : value
    });
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (approvalType.name) {
        if (isNewPage) {
          // create aims document type
          const createApprovalType = {
            name: approvalType.name
          };
          await dispatch(createApprovalTypeAsync(createApprovalType)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('approvalTypeCreateEdit.messages.createSuccess') as string
            })
          );
        } else {
          // update aims document type
          const updateApprovalType = {
            id: approvalType.id,
            name: approvalType.name
          };
          await dispatch(updateApprovalTypeAsync(updateApprovalType)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('approvalTypeCreateEdit.messages.updateSuccess') as string
            })
          );
        }
      } else {
        throw new Error(t('approvalTypeCreateEdit.errors.general') as string);
      }
      setTimeout(() => {
        history.goBack();
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name={isNewPage ? 'masterDataCreate' : 'masterDataSubEdit'}
        data={{
          masterDataName: t('approvalTypeCreateEdit.breadcrumb.approvalType'),
          masterDataRef: 'approval-types',
          masterDataSubName: selectedApprovalType?.name,
          masterDataSubRef: selectedApprovalType?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {isNewPage ? t('approvalTypeCreateEdit.title.new') : t('approvalTypeCreateEdit.title.edit')}
      </Typography>
      <form onSubmit={onSubmit}>
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" gutterBottom>
            {t('approvalTypeCreateEdit.sections.information')}
          </Typography>
          <Divider className={classes.divider} />
          <TextField
            id="approval-type-name"
            label={t('approvalTypeCreateEdit.labels.name')}
            size="small"
            variant="outlined"
            name="name"
            fullWidth
            value={approvalType.name ? approvalType.name : ''}
            onChange={onChangeField}
            required
          />
        </Paper>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={isNewPage ? <PlaylistAddRounded /> : <UpdateRounded />}
          disabled={loading}
        >
          {isNewPage ? t('approvalTypeCreateEdit.buttons.add') : t('approvalTypeCreateEdit.buttons.update')}
        </Button>
      </form>
    </div>
  );
}
